import { useState } from 'react';
import { DeleteButton, EmployeePicker, ErrorBanner, Field, Loading, Modal } from '../components/ui';
import { api } from '../lib/api';
import {
  activityLabel,
  dealBadge,
  formatDate,
  formatNumber,
  humanise,
  serviceLabel,
  todayLocal,
} from '../lib/format';
import { useResource } from '../lib/hooks';
import type {
  Employee,
  SalesAccount,
  SalesAccountDetail,
  SalesActivity,
  SalesContact,
  SalesOpportunity,
  SalesStage,
} from '../lib/types';

/* Numeric fields are held as strings so a half-typed "1." or an emptied box
   does not become 0 under the user's cursor. They convert once, on submit. */
function toNumber(value: string): number {
  const n = Number(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function toNullableNumber(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** A nullable number as a form string. Blank for "not stated", never "null". */
function numberOrBlank(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

const SERVICE_TYPES = [
  'managed_services',
  'co_managed',
  'security',
  'cloud',
  'backup',
  'voice',
  'project',
  'hardware',
  'other',
] as const;

const SOURCES = [
  'referral',
  'outbound',
  'marketing',
  'partner',
  'event',
  'existing_client',
  'other',
] as const;

const ACTIVITY_TYPES = [
  'call',
  'email',
  'meeting',
  'assessment',
  'proposal',
  'qbr',
  'task',
  'note',
] as const;

const DECISION_ROLES = [
  'economic',
  'technical',
  'champion',
  'influencer',
  'user',
  'blocker',
  'unknown',
] as const;

/* =========================================================================
   Deal
   ========================================================================= */

export function DealEditor({
  deal,
  accounts,
  stages,
  employees,
  defaultAccountId,
  onClose,
  onSaved,
}: {
  /** Null creates a new deal. */
  deal: SalesOpportunity | null;
  accounts: SalesAccount[];
  stages: SalesStage[];
  employees: Employee[];
  defaultAccountId?: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const openStage = stages.find((s) => !s.IsClosed);
  /* A new deal starts with no account chosen rather than whichever sorts
     first, which was too easy to save against by mistake. */
  const startingAccountId = deal?.AccountId ?? defaultAccountId ?? null;
  const startingAccount = accounts.find((a) => a.AccountId === startingAccountId) ?? null;
  /* A new deal is pitched to a prospect, so that is all the list offers. An
     existing deal keeps every account, or the one it is already on — now a
     client, once it was won — would vanish from its own dropdown. The account
     just added on the page is kept whatever its type. */
  const choices = deal
    ? accounts
    : accounts.filter((a) => a.AccountType === 'prospect' || a.AccountId === defaultAccountId);

  const [form, setForm] = useState({
    accountId: startingAccountId,
    name: deal?.Name ?? '',
    stageId: deal?.StageId ?? openStage?.StageId ?? stages[0]?.StageId ?? null,
    /* Seats, source and owner are the account's answers before they are the
       deal's. On a NEW deal they start from the account so they do not have to
       be typed twice, and an edit here belongs to this deal alone — nothing is
       written back. On an existing deal the deal's own values win outright,
       including a deliberate blank. */
    ownerEmployeeId: deal ? deal.OwnerEmployeeId : (startingAccount?.OwnerEmployeeId ?? null),
    serviceType: deal?.ServiceType ?? 'managed_services',
    mrr: deal ? String(deal.Mrr) : '',
    oneTimeValue: deal ? String(deal.OneTimeValue) : '',
    termMonths: String(deal?.TermMonths ?? 36),
    seats: deal
      ? (deal.Seats === null || deal.Seats === undefined ? '' : String(deal.Seats))
      : numberOrBlank(startingAccount?.Seats),
    probability:
      deal?.Probability === null || deal?.Probability === undefined ? '' : String(deal.Probability),
    expectedCloseDate: deal?.ExpectedCloseDate?.slice(0, 10) ?? '',
    nextStep: deal?.NextStep ?? '',
    nextStepDate: deal?.NextStepDate?.slice(0, 10) ?? '',
    source: deal ? (deal.Source ?? '') : (startingAccount?.Source ?? ''),
    competitor: deal?.Competitor ?? '',
    lostReason: deal?.LostReason ?? '',
    notes: deal?.Notes ?? '',
  });
  /* A new deal can name a company that is not on the list yet: the account is
     created on Save, just ahead of the deal. Starts on when there is nothing to
     pick from. */
  const [newAccount, setNewAccount] = useState(!deal && choices.length === 0);
  const [newAccountName, setNewAccountName] = useState('');
  /* Kept once created, so a retry after the deal itself fails does not try to
     add the same company twice. */
  const [createdAccountId, setCreatedAccountId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const stage = stages.find((s) => s.StageId === form.stageId);
  const mrr = toNumber(form.mrr);
  const oneTime = toNumber(form.oneTimeValue);
  const term = toNumber(form.termMonths);
  const annual = mrr * 12 + oneTime;
  const tcv = mrr * term + oneTime;
  const probability = toNullableNumber(form.probability) ?? Number(stage?.Probability ?? 0);
  const weighted = (annual * probability) / 100;

  const submit = async () => {
    setSaving(true);
    setError(null);
    let accountId = form.accountId;
    if (newAccount) {
      accountId = createdAccountId;
      if (accountId === null) {
        try {
          const created = await api.post<SalesAccount>('/sales/accounts', {
            name: newAccountName.trim(),
            ownerEmployeeId: form.ownerEmployeeId,
            seats: toNullableNumber(form.seats),
            source: form.source || null,
          });
          accountId = created.AccountId;
          setCreatedAccountId(accountId);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not add the account');
          setSaving(false);
          return;
        }
      }
    }
    const payload = {
      accountId,
      name: form.name.trim(),
      stageId: form.stageId,
      ownerEmployeeId: form.ownerEmployeeId,
      serviceType: form.serviceType,
      mrr,
      oneTimeValue: oneTime,
      termMonths: term,
      seats: toNullableNumber(form.seats),
      probability: toNullableNumber(form.probability),
      expectedCloseDate: form.expectedCloseDate || null,
      nextStep: form.nextStep.trim() || null,
      nextStepDate: form.nextStepDate || null,
      source: form.source || null,
      competitor: form.competitor.trim() || null,
      lostReason: form.lostReason.trim() || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (deal) await api.put(`/sales/opportunities/${deal.OpportunityId}`, payload);
      else await api.post('/sales/opportunities', payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={deal ? deal.Name : 'New deal'}
      onClose={onClose}
      footer={
        <>
          {deal && (
            <DeleteButton
              what={`the "${deal.Name}" deal and its activity`}
              onConfirm={() =>
                void api.del(`/sales/opportunities/${deal.OpportunityId}`).then(onSaved)
              }
            />
          )}
          {/* Between Save and Delete, which is exactly where it belongs: a
              deal that is going nowhere comes off the board without anybody
              having to destroy it to get it out of the way. It is on the
              Archived tab afterwards, and restorable from there. The
              scorecard is untouched — archiving tidies the board, it does
              not rewrite the quarter. */}
          {deal && (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={saving}
              title="Take it off the board. The quarter's figures are unchanged, and nothing is deleted."
              onClick={() =>
                void api
                  .put(`/sales/opportunities/${deal.OpportunityId}/archive`, { archived: true })
                  .then(onSaved)
                  .catch((err: unknown) =>
                    setError(err instanceof Error ? err.message : 'Could not archive'),
                  )
              }
            >
              Archive
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              saving ||
              !form.name.trim() ||
              (newAccount ? !newAccountName.trim() : !form.accountId) ||
              !form.stageId
            }
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Deal">
        <input
          className="input"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="e.g. Managed IT — 42 seats"
          autoFocus
        />
      </Field>

      <div className="form-grid">
        <Field
          label="Account"
          hint={newAccount ? 'Added to the account list when the deal is saved' : undefined}
        >
          {newAccount ? (
            <>
              <input
                className="input"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                placeholder="Company name"
                autoFocus={choices.length > 0}
              />
              {choices.length > 0 && (
                <button
                  type="button"
                  className="link-button xs"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => {
                    setNewAccount(false);
                    setNewAccountName('');
                  }}
                >
                  Pick an existing account
                </button>
              )}
            </>
          ) : (
          <select
            className="select"
            value={form.accountId ?? ''}
            onChange={(e) => {
              if (e.target.value === 'new') {
                setNewAccount(true);
                return;
              }
              const accountId = e.target.value ? Number(e.target.value) : null;
              const picked = accounts.find((a) => a.AccountId === accountId) ?? null;
              /* Changing the account carries its answers across, but only into
                 fields still holding nothing. Overwriting a seat count somebody
                 has just typed because they then corrected the account would
                 lose work silently, which is worse than making them type it. */
              set({
                accountId,
                seats: form.seats === '' ? numberOrBlank(picked?.Seats) : form.seats,
                source: form.source === '' ? (picked?.Source ?? '') : form.source,
                ownerEmployeeId:
                  form.ownerEmployeeId === null
                    ? (picked?.OwnerEmployeeId ?? null)
                    : form.ownerEmployeeId,
              });
            }}
          >
            <option value="">Choose an account</option>
            {!deal && <option value="new">+ New account…</option>}
            {choices.map((a) => (
              <option key={a.AccountId} value={a.AccountId}>
                {a.Name}
              </option>
            ))}
          </select>
          )}
        </Field>
        <Field label="Owner">
          <EmployeePicker
            employees={employees}
            value={form.ownerEmployeeId}
            onChange={(ownerEmployeeId) => set({ ownerEmployeeId })}
            placeholder="Unassigned"
          />
        </Field>
      </div>

      <div className="form-grid">
        <Field
          label="Stage"
          hint={stage?.Description ?? undefined}
        >
          <select
            className="select"
            value={form.stageId ?? ''}
            onChange={(e) => set({ stageId: e.target.value ? Number(e.target.value) : null })}
          >
            {stages.map((s) => (
              <option key={s.StageId} value={s.StageId}>
                {s.Name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Service">
          <select
            className="select"
            value={form.serviceType}
            onChange={(e) => set({ serviceType: e.target.value as typeof form.serviceType })}
          >
            {SERVICE_TYPES.map((t) => (
              <option key={t} value={t}>
                {serviceLabel(t)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* Money. Recurring and one-off are separate fields because an MSP's
          $2k/month contract and a $2k hardware order are not the same deal,
          and one "amount" box makes them look identical on the forecast. */}
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
        <div className="form-grid">
          <Field label="MRR" hint="Recurring, per month">
            <input
              className="input"
              inputMode="decimal"
              value={form.mrr}
              onChange={(e) => set({ mrr: e.target.value })}
              placeholder="0"
            />
          </Field>
          <Field label="One-off" hint="Onboarding, project, hardware">
            <input
              className="input"
              inputMode="decimal"
              value={form.oneTimeValue}
              onChange={(e) => set({ oneTimeValue: e.target.value })}
              placeholder="0"
            />
          </Field>
          <Field label="Term" hint="Months committed">
            <input
              className="input"
              inputMode="numeric"
              value={form.termMonths}
              onChange={(e) => set({ termMonths: e.target.value })}
            />
          </Field>
          <Field label="Seats" hint="Supported users">
            <input
              className="input"
              inputMode="numeric"
              value={form.seats}
              onChange={(e) => set({ seats: e.target.value })}
              placeholder="—"
            />
          </Field>
        </div>

        <div className="deal-value mt-4">
          <div>
            <span className="label">First-year value</span>
            <span className="figure mono">{formatNumber(annual, 'currency')}</span>
          </div>
          <div>
            <span className="label">Weighted at {probability}%</span>
            <span className="figure mono">{formatNumber(weighted, 'currency')}</span>
          </div>
          <div>
            <span className="label">Total contract</span>
            <span className="figure mono">{formatNumber(tcv, 'currency')}</span>
          </div>
        </div>

        <div className="mt-4">
          <Field
            label="Probability override"
            hint={`Blank uses the ${stage?.Name ?? 'stage'} default of ${stage?.Probability ?? 0}%.`}
          >
            <input
              className="input"
              inputMode="numeric"
              value={form.probability}
              onChange={(e) => set({ probability: e.target.value })}
              placeholder={String(stage?.Probability ?? '')}
            />
          </Field>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
        <div className="form-grid">
          <Field label="Expected close">
            <input
              className="input"
              type="date"
              value={form.expectedCloseDate}
              onChange={(e) => set({ expectedCloseDate: e.target.value })}
            />
          </Field>
          <Field label="Next step due">
            <input
              className="input"
              type="date"
              value={form.nextStepDate}
              onChange={(e) => set({ nextStepDate: e.target.value })}
            />
          </Field>
        </div>

        <div className="mt-4">
          <Field
            label="Next step"
            hint="A deal with no next step is a deal nobody is working."
          >
            <input
              className="input"
              value={form.nextStep}
              onChange={(e) => set({ nextStep: e.target.value })}
              placeholder="e.g. Book the network assessment with their IT lead"
            />
          </Field>
        </div>

        <div className="form-grid mt-4">
          <Field label="Source">
            <select
              className="select"
              value={form.source}
              onChange={(e) => set({ source: e.target.value as typeof form.source })}
            >
              <option value="">Not recorded</option>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {humanise(s)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Up against">
            <input
              className="input"
              value={form.competitor}
              onChange={(e) => set({ competitor: e.target.value })}
              placeholder="Incumbent or competitor"
            />
          </Field>
        </div>

        {/* Only asked for on the way out, and always asked for — a lost deal
            with no reason teaches the next quarter nothing. */}
        {stage?.IsClosed && !stage.IsWon && (
          <div className="mt-4">
            <Field label="Why it was lost">
              <textarea
                className="textarea"
                rows={2}
                value={form.lostReason}
                onChange={(e) => set({ lostReason: e.target.value })}
                placeholder="Price, incumbent renewed, no decision, went in-house…"
              />
            </Field>
          </div>
        )}

        <div className="mt-4">
          <Field label="Notes">
            <textarea
              className="textarea"
              rows={2}
              value={form.notes}
              onChange={(e) => set({ notes: e.target.value })}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

/* =========================================================================
   Account
   ========================================================================= */

export function AccountEditor({
  accountId,
  employees,
  onClose,
  onSaved,
  onOpenDeal,
}: {
  /** Null creates a new account. */
  accountId: number | null;
  employees: Employee[];
  onClose: () => void;
  /** The account, when this save CREATED one. The page keeps it so the next
   *  deal or onboarding starts from it rather than from a blank form. */
  onSaved: (created?: SalesAccount) => void;
  onOpenDeal?: (deal: SalesOpportunity) => void;
}) {
  const detail = useResource<SalesAccountDetail>(
    accountId ? `/sales/accounts/${accountId}` : null,
  );

  if (accountId && detail.loading) {
    return (
      <Modal title="Account" onClose={onClose}>
        <Loading label="Loading the account" />
      </Modal>
    );
  }

  return (
    <AccountForm
      account={detail.data}
      employees={employees}
      onClose={onClose}
      onSaved={onSaved}
      onReload={detail.reload}
      onOpenDeal={onOpenDeal}
    />
  );
}

function AccountForm({
  account,
  employees,
  onClose,
  onSaved,
  onReload,
  onOpenDeal,
}: {
  account: SalesAccountDetail | null;
  employees: Employee[];
  onClose: () => void;
  onSaved: (created?: SalesAccount) => void;
  onReload: () => void;
  onOpenDeal?: (deal: SalesOpportunity) => void;
}) {
  const [form, setForm] = useState({
    name: account?.Name ?? '',
    accountType: account?.AccountType ?? 'prospect',
    industry: account?.Industry ?? '',
    website: account?.Website ?? '',
    address: account?.Address ?? '',
    city: account?.City ?? '',
    region: account?.Region ?? '',
    postalCode: account?.PostalCode ?? '',
    seats: account?.Seats === null || account?.Seats === undefined ? '' : String(account.Seats),
    currentMrr:
      account?.CurrentMrr === null || account?.CurrentMrr === undefined
        ? ''
        : String(account.CurrentMrr),
    ownerEmployeeId: account?.OwnerEmployeeId ?? null,
    source: account?.Source ?? '',
    status: account?.Status ?? 'active',
    notes: account?.Notes ?? '',
  });
  /* Only for a NEW account. An existing one edits its people through the
     contacts list below, which knows about there being more than one of them
     and about decision roles; duplicating that here would give two places to
     change the same person. */
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    phone: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contact, setContact] = useState<SalesContact | 'new' | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const setC = (patch: Partial<typeof newContact>) => setNewContact((c) => ({ ...c, ...patch }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      accountType: form.accountType,
      industry: form.industry.trim() || null,
      website: form.website.trim() || null,
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      region: form.region.trim() || null,
      postalCode: form.postalCode.trim() || null,
      seats: toNullableNumber(form.seats),
      currentMrr: toNullableNumber(form.currentMrr),
      ownerEmployeeId: form.ownerEmployeeId,
      source: form.source || null,
      status: form.status,
      notes: form.notes.trim() || null,
    };
    try {
      if (account) {
        await api.put(`/sales/accounts/${account.AccountId}`, payload);
        onSaved();
      } else {
        /* Handed back rather than discarded. A new account is almost never the
           end of the errand — a deal or an onboarding follows within seconds,
           and both ask for things this form has just been told. */
        const created = await api.post<SalesAccount>('/sales/accounts', {
          ...payload,
          /* Ignored server-side without a name — see primaryContactInput. */
          contact: {
            firstName: newContact.firstName.trim() || null,
            lastName: newContact.lastName.trim() || null,
            title: newContact.title.trim() || null,
            email: newContact.email.trim() || null,
            phone: newContact.phone.trim() || null,
          },
        });
        onSaved(created);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        title={account ? account.Name : 'New account'}
        onClose={onClose}
        footer={
          <>
            {account && (
              <DeleteButton
                what={`"${account.Name}", its contacts, deals and activity`}
                onConfirm={() => {
                  /* A refused delete is said, not swallowed: without the catch
                     the window just sat there with the account still in it. */
                  setError(null);
                  api
                    .del(`/sales/accounts/${account.AccountId}`)
                    .then(() => onSaved())
                    .catch((err) => setError(err instanceof Error ? err.message : 'Could not delete the account'));
                }}
              />
            )}
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || !form.name.trim()}
              onClick={() => void submit()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {error && <ErrorBanner message={error} />}

        <Field label="Company">
          <input
            className="input"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            autoFocus
          />
        </Field>

        <div className="form-grid">
          <Field label="Relationship">
            <select
              className="select"
              value={form.accountType}
              onChange={(e) => set({ accountType: e.target.value as typeof form.accountType })}
            >
              <option value="prospect">Prospect</option>
              <option value="client">Client</option>
              <option value="partner">Partner</option>
              <option value="former">Former client</option>
            </select>
          </Field>
          <Field label="Account manager">
            <EmployeePicker
              employees={employees}
              value={form.ownerEmployeeId}
              onChange={(ownerEmployeeId) => set({ ownerEmployeeId })}
            />
          </Field>
        </div>

        <div className="form-grid">
          <Field label="Seats" hint="Supported users">
            <input
              className="input"
              inputMode="numeric"
              value={form.seats}
              onChange={(e) => set({ seats: e.target.value })}
              placeholder="—"
            />
          </Field>
          <Field label="Current MRR" hint="What they pay today">
            <input
              className="input"
              inputMode="decimal"
              value={form.currentMrr}
              onChange={(e) => set({ currentMrr: e.target.value })}
              placeholder="0"
            />
          </Field>
        </div>

        <div className="form-grid">
          <Field label="Industry">
            <input
              className="input"
              value={form.industry}
              onChange={(e) => set({ industry: e.target.value })}
              placeholder="e.g. Professional services"
            />
          </Field>
          <Field label="How they found us">
            <select
              className="select"
              value={form.source}
              onChange={(e) => set({ source: e.target.value as typeof form.source })}
            >
              <option value="">Not recorded</option>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {humanise(s)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* The postal address, in one block. It is the company's — stored on
            the shared client record, not on this account — and the onboarding
            wizard is filled in from it rather than asking again. */}
        <Field label="Street address">
          <input
            className="input"
            value={form.address}
            onChange={(e) => set({ address: e.target.value })}
            placeholder="123 Main St"
          />
        </Field>

        <div className="form-grid">
          <Field label="City">
            <input className="input" value={form.city} onChange={(e) => set({ city: e.target.value })} />
          </Field>
          <Field label="State">
            <input
              className="input"
              value={form.region}
              onChange={(e) => set({ region: e.target.value })}
            />
          </Field>
        </div>

        <div className="form-grid">
          <Field label="ZIP code">
            <input
              className="input"
              value={form.postalCode}
              onChange={(e) => set({ postalCode: e.target.value })}
              placeholder="12345"
            />
          </Field>
          <Field label="Website">
            <input
              className="input"
              value={form.website}
              onChange={(e) => set({ website: e.target.value })}
              placeholder="https://…"
            />
          </Field>
        </div>

        <Field label="Notes">
          <textarea
            className="textarea"
            rows={2}
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </Field>

        {/* The person we deal with, asked for on the way in. Without this the
            first contact is recorded only after the account is saved and
            reopened, which is why so many accounts have none — and it is the
            same person the onboarding wizard asks for two clicks later. */}
        {!account && (
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
            <span className="eyebrow">Primary contact</span>
            <p className="xs subtle mt-2">
              Optional, and recorded as the account's primary contact. Carried into the deal and the
              onboarding, so it is not asked for twice.
            </p>

            <div className="form-grid mt-4">
              <Field label="First name">
                <input
                  className="input"
                  value={newContact.firstName}
                  onChange={(e) => setC({ firstName: e.target.value })}
                  placeholder="Jane"
                />
              </Field>
              <Field label="Last name">
                <input
                  className="input"
                  value={newContact.lastName}
                  onChange={(e) => setC({ lastName: e.target.value })}
                  placeholder="Smith"
                />
              </Field>
            </div>

            <Field label="Job title">
              <input
                className="input"
                value={newContact.title}
                onChange={(e) => setC({ title: e.target.value })}
                placeholder="IT Manager"
              />
            </Field>

            <div className="form-grid">
              <Field label="Email">
                <input
                  className="input"
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setC({ email: e.target.value })}
                  placeholder="jane@company.com"
                />
              </Field>
              <Field label="Phone">
                <input
                  className="input"
                  value={newContact.phone}
                  onChange={(e) => setC({ phone: e.target.value })}
                  placeholder="(555) 000-0000"
                />
              </Field>
            </div>
          </div>
        )}

        {/* Everything below only exists once the account does — a contact
            needs an account id to hang off. */}
        {account && (
          <>
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
              <div className="row-between">
                <span className="eyebrow">Contacts</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setContact('new')}
                >
                  Add contact
                </button>
              </div>

              {account.contacts.length === 0 ? (
                <p className="small muted mt-2">
                  Nobody recorded yet. A deal with no named economic buyer is a deal in trouble.
                </p>
              ) : (
                <div className="stack mt-4" style={{ gap: 'var(--space-2)' }}>
                  {account.contacts.map((c) => (
                    <div key={c.ContactId} className="mini-row">
                      <div className="grow">
                        <div className="strong small">
                          {c.FirstName} {c.LastName}
                          {c.IsPrimary && <span className="badge badge-good ml-2">Primary</span>}
                        </div>
                        <div className="xs muted">
                          {[c.Title, c.Email, c.Phone].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <span className="badge badge-neutral">{humanise(c.DecisionRole)}</span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setContact(c)}
                      >
                        Edit
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
              <span className="eyebrow">Deals</span>
              {account.opportunities.length === 0 ? (
                <p className="small muted mt-2">No deals on this account.</p>
              ) : (
                <div className="stack mt-4" style={{ gap: 'var(--space-2)' }}>
                  {account.opportunities.map((o) => (
                    <button
                      key={o.OpportunityId}
                      type="button"
                      className="mini-row as-button"
                      onClick={() => onOpenDeal?.(o)}
                    >
                      <div className="grow">
                        <div className="strong small">{o.Name}</div>
                        <div className="xs muted">
                          {serviceLabel(o.ServiceType)} ·{' '}
                          {o.IsClosed ? formatDate(o.ClosedOn) : formatDate(o.ExpectedCloseDate)}
                        </div>
                      </div>
                      <span className={`badge ${dealBadge(o)}`}>{o.StageName}</span>
                      <span className="mono small">{formatNumber(o.AnnualValue, 'currency')}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
              <span className="eyebrow">Recent activity</span>
              {account.activities.length === 0 ? (
                <p className="small muted mt-2">Nothing logged yet.</p>
              ) : (
                <ul className="timeline mt-4">
                  {account.activities.slice(0, 8).map((a) => (
                    <li key={a.ActivityId}>
                      <span className="xs mono muted">
                        {formatDate(a.CompletedOn ?? a.DueDate)}
                      </span>
                      <span className="small">
                        <span className="strong">{activityLabel(a.ActivityType)}</span> — {a.Subject}
                        {a.CompletedOn === null && <span className="badge badge-warning ml-2">Open</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </Modal>

      {contact && account && (
        <ContactEditor
          contact={contact === 'new' ? null : contact}
          accountId={account.AccountId}
          onClose={() => setContact(null)}
          onSaved={() => {
            setContact(null);
            onReload();
          }}
        />
      )}
    </>
  );
}

function ContactEditor({
  contact,
  accountId,
  onClose,
  onSaved,
}: {
  contact: SalesContact | null;
  accountId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    firstName: contact?.FirstName ?? '',
    lastName: contact?.LastName ?? '',
    title: contact?.Title ?? '',
    email: contact?.Email ?? '',
    phone: contact?.Phone ?? '',
    isPrimary: contact?.IsPrimary ?? false,
    decisionRole: contact?.DecisionRole ?? 'unknown',
    notes: contact?.Notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      title: form.title.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      isPrimary: form.isPrimary,
      decisionRole: form.decisionRole,
      notes: form.notes.trim() || null,
    };
    try {
      if (contact) await api.put(`/sales/contacts/${contact.ContactId}`, payload);
      else await api.post(`/sales/accounts/${accountId}/contacts`, payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={contact ? `${contact.FirstName} ${contact.LastName}` : 'New contact'}
      onClose={onClose}
      footer={
        <>
          {contact && (
            <DeleteButton
              what={`${contact.FirstName} ${contact.LastName}`}
              onConfirm={() => void api.del(`/sales/contacts/${contact.ContactId}`).then(onSaved)}
            />
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !form.firstName.trim() || !form.lastName.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="form-grid">
        <Field label="First name">
          <input
            className="input"
            value={form.firstName}
            onChange={(e) => set({ firstName: e.target.value })}
            autoFocus
          />
        </Field>
        <Field label="Last name">
          <input
            className="input"
            value={form.lastName}
            onChange={(e) => set({ lastName: e.target.value })}
          />
        </Field>
      </div>

      <div className="form-grid">
        <Field label="Job title">
          <input className="input" value={form.title} onChange={(e) => set({ title: e.target.value })} />
        </Field>
        <Field
          label="Role in the decision"
          hint="Economic buyer signs; champion sells for you internally."
        >
          <select
            className="select"
            value={form.decisionRole}
            onChange={(e) => set({ decisionRole: e.target.value as typeof form.decisionRole })}
          >
            {DECISION_ROLES.map((r) => (
              <option key={r} value={r}>
                {humanise(r)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="form-grid">
        <Field label="Email">
          <input
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => set({ email: e.target.value })}
          />
        </Field>
        <Field label="Phone">
          <input className="input" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
        </Field>
      </div>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={form.isPrimary}
          onChange={(e) => set({ isPrimary: e.target.checked })}
        />
        Primary contact for this account
      </label>

      <Field label="Notes">
        <textarea
          className="textarea"
          rows={2}
          value={form.notes}
          onChange={(e) => set({ notes: e.target.value })}
        />
      </Field>
    </Modal>
  );
}

/* =========================================================================
   Activity
   ========================================================================= */

export function ActivityEditor({
  activity,
  accounts,
  deals,
  employees,
  defaultAccountId,
  onClose,
  onSaved,
}: {
  activity: SalesActivity | null;
  accounts: SalesAccount[];
  deals: SalesOpportunity[];
  employees: Employee[];
  defaultAccountId?: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    accountId: activity?.AccountId ?? defaultAccountId ?? accounts[0]?.AccountId ?? null,
    opportunityId: activity?.OpportunityId ?? null,
    activityType: activity?.ActivityType ?? 'call',
    subject: activity?.Subject ?? '',
    detail: activity?.Detail ?? '',
    dueDate: activity?.DueDate?.slice(0, 10) ?? '',
    completedOn: activity?.CompletedOn?.slice(0, 10) ?? '',
    ownerEmployeeId: activity?.OwnerEmployeeId ?? null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  /* Only deals on the chosen account can be linked — a follow-up filed
     against someone else's deal is worse than one filed against none. */
  const accountDeals = deals.filter((d) => d.AccountId === form.accountId);

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      accountId: form.accountId,
      opportunityId: form.opportunityId,
      activityType: form.activityType,
      subject: form.subject.trim(),
      detail: form.detail.trim() || null,
      dueDate: form.dueDate || null,
      completedOn: form.completedOn || null,
      ownerEmployeeId: form.ownerEmployeeId,
    };
    try {
      if (activity) await api.put(`/sales/activities/${activity.ActivityId}`, payload);
      else await api.post('/sales/activities', payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={activity ? 'Activity' : 'Log activity'}
      onClose={onClose}
      footer={
        <>
          {activity && (
            <DeleteButton
              what={`"${activity.Subject}"`}
              onConfirm={() => void api.del(`/sales/activities/${activity.ActivityId}`).then(onSaved)}
            />
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !form.subject.trim() || !form.accountId}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="form-grid">
        <Field label="Type">
          <select
            className="select"
            value={form.activityType}
            onChange={(e) => set({ activityType: e.target.value as typeof form.activityType })}
          >
            {ACTIVITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {activityLabel(t)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Owner">
          <EmployeePicker
            employees={employees}
            value={form.ownerEmployeeId}
            onChange={(ownerEmployeeId) => set({ ownerEmployeeId })}
          />
        </Field>
      </div>

      <Field label="What happened, or what needs to">
        <input
          className="input"
          value={form.subject}
          onChange={(e) => set({ subject: e.target.value })}
          autoFocus
        />
      </Field>

      <div className="form-grid">
        <Field label="Account">
          <select
            className="select"
            value={form.accountId ?? ''}
            onChange={(e) =>
              set({
                accountId: e.target.value ? Number(e.target.value) : null,
                /* Changing account orphans the deal link. */
                opportunityId: null,
              })
            }
          >
            <option value="">Choose an account</option>
            {accounts.map((a) => (
              <option key={a.AccountId} value={a.AccountId}>
                {a.Name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Deal" hint="Optional — plenty happens before there is one.">
          <select
            className="select"
            value={form.opportunityId ?? ''}
            onChange={(e) => set({ opportunityId: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Not tied to a deal</option>
            {accountDeals.map((d) => (
              <option key={d.OpportunityId} value={d.OpportunityId}>
                {d.Name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="form-grid">
        <Field label="Due">
          <input
            className="input"
            type="date"
            value={form.dueDate}
            onChange={(e) => set({ dueDate: e.target.value })}
          />
        </Field>
        <Field label="Completed">
          <input
            className="input"
            type="date"
            value={form.completedOn}
            onChange={(e) => set({ completedOn: e.target.value })}
          />
        </Field>
      </div>

      {form.completedOn === '' && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => set({ completedOn: todayLocal() })}
        >
          Mark done today
        </button>
      )}

      <Field label="Detail">
        <textarea
          className="textarea"
          rows={3}
          value={form.detail}
          onChange={(e) => set({ detail: e.target.value })}
        />
      </Field>
    </Modal>
  );
}
